import { ChangeEvent, useEffect, useState } from 'react';
import { Close, Search } from '@material-ui/icons';
import { Chip } from '../Chip';
import { Switch } from '../Switch';
import { Button } from '../Button/NewButton';
import { IconButton } from '../IconButton';
import { Input } from '../Input';
import { ConfirmModal } from '../ConfirmModal';
import clsx from 'clsx';

interface Props {
  allItems: ChipList[];
  classes?: {
    listContainer?: string;
  };
  chipClasses?: {
    default?: string;
    outlined?: string;
  };
  editing: boolean;
  existingItems: ChipList[];
  externalSave?: boolean;
  noItemsMessage?: string;
  onChange?(addedItems: string[], removedItems: string[]): void;
  onSave?(addedItems: string[], removedItems: string[]): void;
  saveRequired?: boolean;
}

export const ChipList = ({
  allItems,
  classes,
  chipClasses,
  editing,
  noItemsMessage,
  existingItems,
  externalSave = false,
  onChange = () => null,
  onSave,
  saveRequired = true,
}: Props) => {
  const [existing, setExisting] = useState(existingItems);
  const [addedItems, setAddedItems] = useState<string[]>([]);
  const [removedItems, setRemovedItems] = useState<string[]>([]);
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);
  const [searchFor, setSearchFor] = useState('');
  const [sortByAsc, setSortByAsc] = useState(true);
  const [existingOnly, setExistingOnly] = useState(false);

  const chipIsAddedOrExisting = (chip: ChipList) =>
    addedItems.includes(chip.id) || existing.find((e) => e.id === chip.id);

  const sortSelectedChips = (a: any, b: any) => {
    if (chipIsAddedOrExisting(a) && chipIsAddedOrExisting(b)) return 0;
    if (chipIsAddedOrExisting(a) && !chipIsAddedOrExisting(b)) return -1;

    return 1;
  };
  const sortByNameAsc = (a: any, b: any) => (a.name < b.name ? -1 : 1);
  const sortByNameDesc = (a: any, b: any) => (a.name > b.name ? -1 : 1);

  useEffect(() => {
    setExisting(existingItems);
  }, [existingItems.length]);

  const noDataMessage = (
    <p className="text-lg opacity-70">
      {noItemsMessage || 'No items to display'}
    </p>
  );

  useEffect(() => {
    if (!editing) resetState();
  }, [editing]);

  useEffect(() => {
    if (addedItems.length || removedItems.length) {
      onChange(addedItems, removedItems);
    }
  }, [addedItems.length, removedItems.length]);

  const resetState = () => {
    setAddedItems([]);
    setRemovedItems([]);
    setExisting(existingItems);
    setSortByAsc(true);
    setSearchFor('');
    setExistingOnly(false);
  };

  const canSave = addedItems.length || removedItems.length;

  const handleAdd = (id: string) => {
    const matchedItem = allItems.find((i) => i.id === id);
    if (!addedItems.includes(id))
      setAddedItems((addedItems) => [id, ...addedItems]);
    if (matchedItem !== undefined)
      setExisting((existing) => [matchedItem, ...existing]);
  };

  const handleDelete = (id: string) => {
    if (existingItems.find((i) => i.id === id)) {
      setRemovedItems((removedItems) => [id, ...removedItems]);
    }
    setAddedItems((addedItems) => addedItems.filter((item) => item !== id));
    setExisting((existing) => existing.filter((item) => id !== item.id));
    onChange(addedItems, removedItems);
  };

  const handleSave = () => {
    if (onSave !== undefined) {
      onSave(addedItems, removedItems);
      resetState();
      setConfirmExitOpen(false);
    }
  };

  const allChips = allItems.length
    ? allItems
        .filter((c) => c.name.toLowerCase().includes(searchFor.toLowerCase()))
        .filter((c) => {
          if (existingOnly)
            return existingItems.map((i) => i.id).includes(c.id);
          else return c;
        })
        .sort(sortByAsc ? sortByNameAsc : sortByNameDesc)
        .sort(sortSelectedChips)
        .map((item) => {
          const hasMatch = Boolean(existing.find((i) => i.id === item.id));
          return (
            <Chip
              key={item.id}
              classNames={{
                root: clsx({
                  [chipClasses?.default ?? '']: hasMatch,
                  [chipClasses?.outlined ?? '']: !hasMatch,
                }),
              }}
              label={item.name}
              variant={hasMatch ? 'default' : 'outlined'}
              size="small"
              clickable={!hasMatch}
              onDelete={hasMatch ? () => handleDelete(item.id) : undefined}
              onClick={() => handleAdd(item.id)}
            />
          );
        })
    : noDataMessage;

  const existingChips = existing.length
    ? existing
        .sort((a, b) => (a.name < b.name ? -1 : 1))
        .map((item) => (
          <Chip
            classNames={{
              root: chipClasses?.outlined ?? '',
            }}
            key={item.id}
            label={item.name}
            variant="outlined"
            size="small"
          />
        ))
    : noDataMessage;

  return (
    <>
      <div className="flex flex-col pb-2">
        {editing ? (
          <div className="flex flex-row w-full justify-between items-center mb-3 gap-3">
            <div className="flex flex-row items-center justify-center gap-3">
              <Input
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setSearchFor(event.target.value)
                }
                placeholder="Search"
                value={searchFor}
                variant="contained"
                startAdornment={<Search fontSize="small" />}
              />
              {searchFor !== '' ? (
                <IconButton onClick={() => setSearchFor('')}>
                  <Close fontSize="small" />
                </IconButton>
              ) : null}
              <Button
                className="!text-xs !text-primary"
                onClick={() => setSortByAsc(!sortByAsc)}
                padding="none"
                theme="text"
                typeCasing="capitalize"
              >
                {sortByAsc ? 'Sort Desc' : 'Sort Asc'}
              </Button>
              {existingItems.length ? (
                <Switch
                  color="primary"
                  checked={existingOnly}
                  label={existingOnly ? 'Existing' : 'All'}
                  direction="row"
                  position="end"
                  size="small"
                  onClick={() => setExistingOnly(!existingOnly)}
                />
              ) : null}
            </div>

            {canSave && saveRequired && !externalSave ? (
              <Button
                className="!text-xs !text-primary"
                onClick={handleSave}
                padding="none"
                theme="text"
              >
                Save
              </Button>
            ) : null}
          </div>
        ) : null}
        <div
          className={clsx(
            'flex flex-row flex-wrap w-full max-h-[120px] overflow-auto gap-2',
            classes?.listContainer ?? ''
          )}
        >
          {editing ? allChips : existingChips}
        </div>
      </div>
      <ConfirmModal
        open={confirmExitOpen}
        onClose={() => setConfirmExitOpen(false)}
        onConfirm={() => {
          setConfirmExitOpen(false);
          resetState();
        }}
        header="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to exit?"
      />
    </>
  );
};
